import { generateUuid } from './uuid.js'
import { widgetChatDebug } from './widgetChatDebug.js'

/**
 * Chat transport for the v2 widget.
 *
 * Layered fallback strategy (highest priority first):
 *   1. SSE streaming via `POST {streamingEndpoint}` - the hot path.
 *      Server emits `event: token` with `data: {"delta":"..."}`, then
 *      optionally `event: citations` (filtered sources after structured
 *      JSON completes), optionally `event: suggested_prompts` (LLM follow-up
 *      prompts), then `event: done`. Some agents have streaming disabled
 *      backend-side; in that case the same endpoint replies with a
 *      JSON body and a non-text/event-stream content-type, which we
 *      detect and parse as the JSON shape.
 *   2. JSON fallback via `POST {fallbackEndpoint}` (`/api/v1/widget/chat/respond`).
 *   3. Legacy `chatServer` URL (FormData POST that v1 used). Still
 *      available if a customer has a frozen on-prem ochatbot install
 *      and the v2 backend isn't configured for them yet.
 *
 * The transport surfaces a small imperative API that mirrors the legacy
 * `Ochatbot` callbacks (`onMessage`, `onTyping`, `onDone`, `onError`)
 * so existing message-rendering code keeps working when wired through
 * `LayoutManager`. Each `send()` returns an `AbortController` so chat
 * surfaces can cancel in-flight requests on close/route change.
 */
export class ChatTransport {
  constructor(options = {}) {
    this.options = { ...options }
    this.activeController = null
  }

  configure(options = {}) {
    this.options = { ...this.options, ...options }
  }

  abort() {
    if (this.activeController) {
      try {
        this.activeController.abort()
      } catch {
        // ignore
      }
    }
    this.activeController = null
  }

  /**
   * Send a chat message. Returns a Promise that resolves once the
   * server-side turn completes (or fallback completes). Streaming
   * tokens are surfaced through `handlers.onToken`. Structured
   * messages (buttons/list/action) come through `handlers.onMessage`.
   */
  async send(input, handlers = {}) {
    this.abort()
    const controller = new AbortController()
    this.activeController = controller

    const payload = buildPayload(input, this.options)
    const messageId = payload.streamingMessageId

    // Ensure typing indicator is immediate even when streaming handshake is slow.
    handlers.onTyping?.(true)

    widgetChatDebug('send:start', {
      streamingMessageId: messageId,
      streamingEndpoint: this.options.streamingEndpoint,
      fallbackEndpoint: this.options.fallbackEndpoint,
      streamingEnabled: this.options.streamingEnabled !== false,
      agentId: this.options.agentId,
      messageLen: (payload.body?.message || '').length,
    })

    try {
      const streamed = await this._tryStreaming(
        payload,
        controller.signal,
        handlers,
        messageId,
      )
      if (streamed) {
        widgetChatDebug('send:done', { mode: 'stream' })
        return { ok: true, mode: 'stream' }
      }
      const json = await this._tryJsonFallback(
        payload,
        controller.signal,
        handlers,
      )
      if (json) {
        widgetChatDebug('send:done', { mode: 'json' })
        return { ok: true, mode: 'json' }
      }
      const legacy = await this._tryLegacy(
        payload,
        controller.signal,
        handlers,
      )
      if (legacy) {
        widgetChatDebug('send:done', { mode: 'legacy' })
        return { ok: true, mode: 'legacy' }
      }
      throw new Error('No chat transport available')
    } catch (error) {
      if (error.name === 'AbortError') {
        handlers.onTyping?.(false)
        handlers.onAbort?.()
        return { ok: false, aborted: true }
      }
      handlers.onTyping?.(false)
      handlers.onError?.(error)
      return { ok: false, error }
    } finally {
      if (this.activeController === controller) this.activeController = null
    }
  }

  async _tryStreaming(payload, signal, handlers, messageId) {
    const { streamingEndpoint, streamingEnabled } = this.options
    if (!streamingEndpoint || streamingEnabled === false) return false

    let response
    try {
      response = await fetch(streamingEndpoint, {
        method: 'POST',
        signal,
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream, application/json',
          ...this._authHeaders(),
        },
        body: JSON.stringify(payload.body),
      })
    } catch (error) {
      if (error.name === 'AbortError') throw error
      widgetChatDebug('_tryStreaming:fetch_error', { name: error.name, message: error.message })
      return false
    }

    if (!response.ok) {
      widgetChatDebug('_tryStreaming:http_not_ok', {
        status: response.status,
        statusText: response.statusText,
        contentType: response.headers.get('content-type'),
      })
      // Auth/CORS/rate-limit errors should fall through to JSON
      // fallback - the streaming controller transparently downgrades
      // for disabled agents but otherwise returns 4xx/5xx.
      return false
    }

    const contentType = response.headers.get('content-type') || ''
    widgetChatDebug('_tryStreaming:http_ok', {
      status: response.status,
      contentType,
      isEventStream: /text\/event-stream/i.test(contentType),
    })
    if (!/text\/event-stream/i.test(contentType)) {
      // Streaming was disabled for this agent and the server returned
      // the JSON shape from `/respond`. Hand it to the JSON parser.
      const json = await response.json()
      widgetChatDebug('_tryStreaming:parsed_as_json_not_sse', {
        jsonKeys: json && typeof json === 'object' ? Object.keys(json) : [],
        hasMessagesArray: Array.isArray(json?.messages),
        hasMessageString: typeof json?.message === 'string',
      })
      this._dispatchJsonResponse(json, handlers)
      return true
    }

    if (!response.body) {
      widgetChatDebug('_tryStreaming:no_response_body')
      return false
    }
    handlers.onTyping?.(true)
    handlers.onStreamStart?.({ messageId })
    const sseStats = {
      token: 0,
      message: 0,
      done: 0,
      citations: 0,
      suggested_prompts: 0,
      emptyTokenPayload: 0,
    }
    await consumeSse(response.body, signal, {
      onToken: (delta) => handlers.onToken?.({ messageId, delta }),
      onMessage: (msg) => handlers.onMessage?.(msg),
      onCitations: (payload) => handlers.onCitations?.({ messageId, ...payload }),
      onSuggestedPrompts: (payload) =>
        handlers.onSuggestedPrompts?.({ messageId, ...payload }),
      onDone: () => {
        handlers.onTyping?.(false)
        handlers.onStreamEnd?.({ messageId })
        handlers.onDone?.()
      },
    }, sseStats)
    widgetChatDebug('_tryStreaming:sse_complete', {
      streamingMessageId: messageId,
      sseStats,
    })
    return true
  }

  async _tryJsonFallback(payload, signal, handlers) {
    const { fallbackEndpoint } = this.options
    if (!fallbackEndpoint) return false

    handlers.onTyping?.(true)
    let response
    try {
      response = await fetch(fallbackEndpoint, {
        method: 'POST',
        signal,
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...this._authHeaders(),
        },
        body: JSON.stringify(payload.body),
      })
    } catch (error) {
      handlers.onTyping?.(false)
      if (error.name === 'AbortError') throw error
      return false
    }

    handlers.onTyping?.(false)
    if (!response.ok) return false
    const json = await response.json()
    this._dispatchJsonResponse(json, handlers)
    return true
  }

  async _tryLegacy(payload, signal, handlers) {
    const { chatServer } = this.options
    if (!chatServer) return false

    const form = new FormData()
    for (const [key, value] of Object.entries(payload.legacyForm)) {
      if (value == null) continue
      if (typeof value === 'object') {
        form.append(key, JSON.stringify(value))
      } else {
        form.append(key, String(value))
      }
    }

    handlers.onTyping?.(true)
    let response
    try {
      response = await fetch(chatServer, {
        method: 'POST',
        signal,
        credentials: 'omit',
        body: form,
      })
    } catch (error) {
      handlers.onTyping?.(false)
      if (error.name === 'AbortError') throw error
      return false
    }
    handlers.onTyping?.(false)
    if (!response.ok) return false
    const json = await response.json()
    this._dispatchJsonResponse(json, handlers)
    return true
  }

  _authHeaders() {
    const { authToken, masterKey } = this.options
    const headers = {}
    if (authToken) headers.Authorization = `Bearer ${authToken}`
    const mk =
      typeof masterKey === 'string' ? masterKey.trim() : String(masterKey || '').trim()
    if (mk) headers['X-Master-Key'] = mk
    return headers
  }

  _dispatchJsonResponse(json, handlers) {
    const root = unwrapWidgetChatJson(json)
    let messages = []
    if (Array.isArray(root?.messages)) {
      messages = root.messages
    } else if (Array.isArray(root)) {
      messages = root
    } else if (root && typeof root === 'object') {
      // `/api/v1/widget/chat/respond` and streaming downgrade return
      // `{ message: "plain text", finishReason, ... }` — not `messages[]`.
      const plain =
        typeof root.message === 'string'
          ? root.message
          : typeof root.text === 'string'
            ? root.text
            : typeof root.body === 'string'
              ? root.body
              : ''
      if (plain.trim()) {
        const suggestedPrompts = normalizeSuggestedPromptsFromJson(root)
        const language =
          typeof root.language === 'string' && root.language.trim()
            ? root.language.trim()
            : null
        messages = [
          {
            from: 'chatbot',
            type: 'text',
            text: plain,
            ...(Array.isArray(root.citations) && root.citations.length > 0
              ? { citations: root.citations }
              : {}),
            ...(suggestedPrompts.length > 0 ? { suggestedPrompts } : {}),
            ...(language ? { language } : {}),
          },
        ]
      } else if (root.type || root.buttons) {
        messages = [root]
      } else if (root.attachment && typeof root.attachment === 'object') {
        messages = [
          {
            from: 'chatbot',
            type: 'text',
            text: '',
            attachment: root.attachment,
          },
        ]
      }
    }
    for (const msg of messages) {
      const stamped = {
        ...msg,
        from: msg.from || 'chatbot',
        time: msg.time || new Date().toJSON(),
        id: msg.id || generateUuid(),
      }
      widgetChatDebug('_dispatchJsonResponse:onMessage', {
        type: stamped.type,
        id: stamped.id,
        textLen: typeof stamped.text === 'string' ? stamped.text.length : 0,
      })
      handlers.onMessage?.(stamped)
    }
    widgetChatDebug('_dispatchJsonResponse:done', { dispatchedCount: messages.length })
    handlers.onDone?.()
  }
}

/**
 * Encode `currentPage` the way the legacy widget does. The v2 backend
 * (`StreamChatInput::messageWithPageContext`) decodes base64 and then
 * URL-decodes the result, so encoding the page url with `encodeURIComponent`
 * twice and base64-wrapping it round-trips cleanly.
 */
export function encodeCurrentPage(url) {
  if (!url) return ''
  try {
    const encoded = encodeURIComponent(url)
    if (typeof btoa === 'function') return btoa(encoded)
    return encoded
  } catch {
    return ''
  }
}

/**
 * Laravel API resources often wrap payloads in `{ data: { ... } }`.
 * Normalize so message extraction matches the flat widget contract.
 */
function unwrapWidgetChatJson(json) {
  if (!json || typeof json !== 'object') return json
  const data = json.data
  if (
    data &&
    typeof data === 'object' &&
    (Array.isArray(data.messages) ||
      typeof data.message === 'string' ||
      typeof data.text === 'string' ||
      data.type ||
      data.buttons)
  ) {
    return data
  }
  return json
}

function normalizeAgentIdForPayload(raw) {
  if (raw == null || raw === '') return undefined
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : raw
}

function buildPayload(input, options) {
  const messageId = generateUuid()
  const currentPage = options.currentPage || input.currentPage || ''
  const masterRaw = options.masterKey || input.masterKey
  const master =
    typeof masterRaw === 'string' ? masterRaw.trim() : String(masterRaw || '').trim()
  const body = {
    message: input.text ?? input.message ?? '',
    sessionId: options.sessionId || input.sessionId || generateUuid(),
    agentId: normalizeAgentIdForPayload(options.agentId || input.agentId),
    masterKey: master || undefined,
    master_key: master || undefined,
    currentPage: encodeCurrentPage(currentPage),
    interactive: input.interactive ?? false,
    buttonText: input.buttonText ?? '',
    attachment: input.attachment ?? null,
    isWebAi: Boolean(options.isWebAi || input.isWebAi),
    streamingMessageId: messageId,
  }
  const legacyForm = {
    driver: 'web',
    userId: options.userId || body.sessionId,
    message: body.message,
    interactive: body.interactive ? 1 : 0,
    buttonText: body.buttonText,
    currentPage: body.currentPage,
    isWebAi: body.isWebAi ? 1 : 0,
    attachment: body.attachment ? JSON.stringify(body.attachment) : '',
  }
  return { body, legacyForm, streamingMessageId: messageId }
}

/**
 * Tiny SSE parser. We don't pull in `eventsource-parser` because adding
 * a transitive dep for ~300 bytes of state machine eats into the bundle
 * budget. The parser handles `event:`, `data:`, comment lines, and
 * multi-line `data:` values per the SSE spec.
 */
async function consumeSse(stream, signal, handlers, stats = null) {
  const reader = stream.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''
  let event = 'message'
  let dataLines = []
  let doneFired = false
  const fireDone = () => {
    if (doneFired) return
    doneFired = true
    handlers.onDone?.()
  }

  const dispatch = () => {
    if (dataLines.length === 0 && event === 'message') return
    const dataString = dataLines.join('\n')
    dataLines = []
    const eventName = event
    event = 'message'
    if (eventName === 'token') {
      const parsed = safeJson(dataString)
      const delta = parsed?.delta ?? parsed?.text ?? ''
      if (delta) {
        if (stats) stats.token += 1
        handlers.onToken?.(delta)
      } else if (stats) {
        stats.emptyTokenPayload += 1
      }
      return
    }
    if (eventName === 'message') {
      const parsed = safeJson(dataString)
      if (parsed) {
        if (stats) stats.message += 1
        handlers.onMessage?.(parsed)
      }
      return
    }
    if (eventName === 'done') {
      if (stats) stats.done += 1
      fireDone()
      return
    }
    if (eventName === 'citations') {
      const parsed = safeJson(dataString)
      if (parsed) {
        if (stats) stats.citations += 1
        handlers.onCitations?.(parsed)
      }
      return
    }
    if (eventName === 'suggested_prompts') {
      const parsed = safeJson(dataString)
      if (parsed) {
        if (stats) stats.suggested_prompts += 1
        handlers.onSuggestedPrompts?.(parsed)
      }
      return
    }
    // Unknown events are ignored on purpose; the streaming MVP documents
    // `token`, `message`, `citations`, `suggested_prompts`, and `done`.
  }

  while (!signal.aborted) {
    const { value, done } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let newlineIdx
    while ((newlineIdx = buffer.indexOf('\n')) !== -1) {
      const rawLine = buffer.substring(0, newlineIdx).replace(/\r$/, '')
      buffer = buffer.substring(newlineIdx + 1)
      if (rawLine === '') {
        dispatch()
        continue
      }
      if (rawLine.startsWith(':')) continue
      if (rawLine.startsWith('event:')) {
        event = rawLine.substring(6).trim()
      } else if (rawLine.startsWith('data:')) {
        dataLines.push(rawLine.substring(5).replace(/^\s/, ''))
      }
    }
  }
  if (buffer.length > 0) {
    // Treat trailing un-terminated data as an event boundary.
    dispatch()
  }
  fireDone()
}

function safeJson(input) {
  if (!input) return null
  try {
    return JSON.parse(input)
  } catch {
    return null
  }
}

/** @param {Record<string, unknown>} root */
function normalizeSuggestedPromptsFromJson(root) {
  const raw = root.suggestedPrompts ?? root.suggested_prompts
  if (!Array.isArray(raw)) return []
  const out = []
  for (const item of raw) {
    const text = String(item ?? '').trim()
    if (text) out.push(text)
  }
  return out
}
